/**
 * ClickHouse serializes DateTime values as "YYYY-MM-DD HH:MM:SS" in UTC (no `T` / `Z`).
 * Individual event timestamps in drill-down tables are shown in the viewer's browser
 * timezone so they match local expectations. Suite timezone is reserved for server-side
 * day/hour bucketing on charts — not for raw event_time cells.
 */

export const parseClickHouseUtcDateTime = (raw: unknown): Date | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    const d = new Date(raw > 1e12 ? raw : raw * 1000);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const str = String(raw).trim();
  if (/^\d+(\.\d+)?$/.test(str)) {
    const n = Number(str);
    if (Number.isFinite(n) && n > 0) {
      const d = new Date(n > 1e12 ? n : n * 1000);
      return Number.isFinite(d.getTime()) ? d : null;
    }
  }
  const isoish = /^\d{4}-\d{2}-\d{2} /.test(str) ? `${str.replace(' ', 'T')}Z` : str;
  const normalized = isoish.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoish) ? isoish : `${isoish}Z`;
  const d = new Date(normalized);
  return Number.isFinite(d.getTime()) ? d : null;
};

/** Format a ClickHouse UTC event timestamp for UI display in the browser timezone. */
export const formatEventTimestampLocal = (raw: unknown): string => {
  const d = parseClickHouseUtcDateTime(raw);
  if (!d) {
    if (raw === null || raw === undefined || raw === '' || raw === 0 || raw === '0') return '';
    return String(raw);
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
};
